import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { MdEdit, MdDelete, MdFilterList, MdAddCircle } from "react-icons/md";
import Tooltip from "@mui/material/Tooltip";
import { ToastContainer, toast } from "react-toastify";

import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";

const TDSMaster = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const [isLoading, setIsLoading] = useState(true);
  const [tdsRows, setTdsRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  const formatPercent = (v) =>
    v === null || v === undefined || v === "" ? "" : `${Number(v)}%`;
  const formatAmount = (v) =>
    v === null || v === undefined || v === "" ? "" : new Intl.NumberFormat().format(Number(v));
  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return iso;
    }
  };

  const normalizeRow = (row) => ({
    tds_id: row.tds_id,
    account_code: row.acc_code,
    effective_date: row.effect_date,
    firm_tds_percent: row.firm_tds_percent,
    individual_tds_percent: row.individual_tds_percent,
    without_pan_tds_percent: row.without_pan_tds_percentage,
    interest_percent: row.interest_percentage,
    single_limit_amount: row.single_limit_amount,
    aggregate_limit_amount: row.aggregate_limit_amount,
    account_master_id: row.account_master_id,
    is_active: row.is_active,
    codedisabled: row.codedisabled,
  });

  const loadTdsData = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}/finance/searchtds_master`,
        {
          tds_id: 0,
          account_master_id: 0,
          effective_date_from: null,
          effective_date_to: null,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = res.data || [];
      setTdsRows(data.map(normalizeRow));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load TDS data");
      setTdsRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (tds_id) => {
    const row = tdsRows.find((r) => r.tds_id === tds_id);
    if (row) navigate("/finance/tds/add", { state: { tdsData: row } });
  };

  // 🔹 Change status using inactive_tds_master (no full update)
  const handleDeleteClick = async (tds_id) => {
    const confirmDelete = window.confirm("Are you sure you want change the status?");
    if (!confirmDelete) return;

    try {
      const modifiedBy =
        userObj.user_name || userObj.login_id || userObj.email_id || "SYSTEM";

      const res = await axios.post(
        `${baseUrl}/finance/inactive_tds_master`,
        {
          tds_id,
          modified_by: modifiedBy,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.status < 200 || res.status >= 300) {
        throw new Error(`HTTP ${res.status}`);
      }

      const { message } = res.data || {};
      toast.success(message || "Record set to InActive");
      await loadTdsData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to change status");
    }
  };

  const baseColumns = [
    {
      field: "account_code",
      headerName: "Account Code",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span className="cell-wrap">{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "effective_date",
      headerName: "Effective Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      valueGetter: (params) => params.row?.effective_date,
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },
    {
      field: "firm_tds_percent",
      headerName: "Firm TDS%",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (p) => <span>{formatPercent(p.value)}</span>,
    },
    {
      field: "individual_tds_percent",
      headerName: "Individual TDS%",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (p) => <span>{formatPercent(p.value)}</span>,
    },
    {
      field: "without_pan_tds_percent",
      headerName: "Without PAN TDS%",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (p) => <span>{formatPercent(p.value)}</span>,
    },
    {
      field: "interest_percent",
      headerName: "Interest %",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (p) => <span>{formatPercent(p.value)}</span>,
    },
    {
      field: "single_limit_amount",
      headerName: "Single Limit Amount",
      flex: 1.2,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (p) => <span>{formatAmount(p.value)}</span>,
    },
    {
      field: "aggregate_limit_amount",
      headerName: "Aggregate Limit Amount",
      flex: 1.2,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (p) => <span>{formatAmount(p.value)}</span>,
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => {
        const active = params.row?.is_active;
        const statusLabel =
          active === false || active === "false" ? "InActive" : "Active";
        return (
          <Tooltip title={statusLabel}>
            <span>{statusLabel}</span>
          </Tooltip>
        );
      },
    },
  ];

  const actionColumn = {
    field: "actions",
    headerName: "Actions",
    headerAlign: "center",
    align: "center",
    sortable: false,
    flex: 1,
    renderCell: (params) =>
      params.row.tds_id !== "No records found" ? (
        <div className="tableActions">
          <Tooltip title="Edit" arrow>
            <span
              className="commonActionIcons"
              onClick={() => handleEditClick(params.row.tds_id)}
            >
              <MdEdit />
            </span>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <span
              className="commonActionIcons"
              onClick={() => handleDeleteClick(params.row.tds_id)}
            >
              <MdDelete />
            </span>
          </Tooltip>
        </div>
      ) : null,
  };

  const columns = canSubmit ? [...baseColumns, actionColumn] : [...baseColumns];

  useEffect(() => {
    loadTdsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchableColumns = [(row) => row.account_code, (row) => row.effective_date];

  const filteredRecords = (tdsRows || []).filter((r) =>
    searchableColumns.some((selector) =>
      String(selector(r) || "").toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const [filter, setFilter] = useState({
    account_code: "",
    effective_date_from: "",
    effective_date_to: "",
    school_id: userObj.school_id,
    action: "FILTER",
  });

  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}/finance/searchtds_master`,
        {
          tds_id: 0,
          account_master_id: 0,
          effective_date_from: filter.effective_date_from || null,
          effective_date_to: filter.effective_date_to || null,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      let data = (res.data || []).map(normalizeRow);

      if (filter.account_code) {
        const q = filter.account_code.toLowerCase();
        data = data.filter((r) => (r.account_code || "").toLowerCase().includes(q));
      }

      setTdsRows(data);
      setShowFilterModal(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 404) {
        setTdsRows([]);
      } else {
        toast.error("Failed to fetch filtered data. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterClear = async () => {
    setFilter({
      account_code: "",
      effective_date_from: "",
      effective_date_to: "",
      school_id: userObj.school_id,
      action: "FILTER",
    });
    await loadTdsData();
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">TDS Master</h6>
          </div>
          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
            >
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            {canSubmit && (
              <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
                <Button
                  className="primaryBtn"
                  variant="primary"
                  onClick={() => navigate("/finance/tds/add")}
                >
                  <MdAddCircle />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      <div className="commonTable height100">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords}
              columns={columns}
              getRowId={(r) => r.tds_id}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={500}
            />
          )}
        </div>
      </div>

      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form id="filterForm" onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="accountCode">
                    <Form.Label>Account Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter account code"
                      maxLength={35}
                      value={filter.account_code}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^[a-zA-Z0-9\s\-_/]*$/.test(v)) {
                          setFilter({ ...filter, account_code: v });
                        }
                      }}
                    />
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="effectiveFrom">
                    <Form.Label>Effective From</Form.Label>
                    <Form.Control
                      type="date"
                      value={filter.effective_date_from}
                      onChange={(e) =>
                        setFilter({ ...filter, effective_date_from: e.target.value })
                      }
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <div>
            <Button variant="secondary" className="btn-info clearBtn" onClick={handleFilterClear}>
              Reset
            </Button>
          </div>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn"
              onClick={handleCloseFilterModal}
            >
              Close
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              type="submit"
              form="filterForm"
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TDSMaster;
